import { useQuery } from '@tanstack/react-query';
import { PkCard, PkCarousel, PkSpinner } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useMemo, useRef, useState } from 'react';

import { getPhotogalleryByIdQuery } from '@queries/editorial';
import PictureHelper from '@shared/PictureHelper';
import GoogleTagManagerHelper from '@tracking/gtm-helper';

import * as styles from './styles.module.scss';

const imgFormats = ['0@w1'];

const PhotoCarousel = ({ photogalleryId, isExpanded = false }: { photogalleryId: string; isExpanded?: boolean }) => {
  const { data: photos } = useQuery(getPhotogalleryByIdQuery(photogalleryId));
  const [currentIndex, setCurrentIndex] = useState(0);

  const itemRef = useRef<HTMLPkCarouselElement>(null);

  const nextSlide = () => {
    const index = currentIndex + 1;
    if (index < photos.length) {
      setCurrentIndex(index);
      GoogleTagManagerHelper.ga4Event('photo_detail', { section_reference: 'photo_detail', interaction: 'Next image' });
    }
  };

  const prevSlide = () => {
    const index = currentIndex - 1;
    if (index >= 0) {
      setCurrentIndex(index);
      GoogleTagManagerHelper.ga4Event('photo_detail', {
        section_reference: 'photo_detail',
        interaction: 'Previous image',
      });
    }
  };

  const breakpoints = useMemo(
    () =>
      isExpanded
        ? {
            0: {
              slidesPerView: 1.1,
              slidesPerGroup: 1,
              spaceBetween: 12,
            },
            768: {
              slidesPerView: 2.1,
              slidesPerGroup: 2,
              spaceBetween: 24,
            },
          }
        : {
            0: {
              slidesPerView: 1.1,
              slidesPerGroup: 1,
              spaceBetween: 12,
            },
            768: {
              slidesPerView: 1,
              spaceBetween: 24,
            },
          },

    [isExpanded],
  );

  return (
    <div
      className={clsx({
        [styles.container]: true,
        [styles.empty]: !photos.length,
      })}
    >
      {photos.length ? (
        <>
          <PkCarousel
            className='pk-theme--light'
            breakpoints={breakpoints}
            navigationPosition='inside'
            ref={itemRef}
            onPkClickNext={nextSlide}
            onPkClickPrev={prevSlide}
            onPkSlideNext={nextSlide}
            onPkSlidePrev={prevSlide}
          >
            {photos.map((photo) => (
              <div key={`item_${photo.id}`} className={styles.slide}>
                <PkCard content='default'>
                  <div slot='heading'>
                    <PictureHelper formats={imgFormats} image={photo.image} title={photo.title} />
                  </div>
                </PkCard>
              </div>
            ))}
          </PkCarousel>
          <div className={styles.panel}>
            <div className={styles.index}>{`${currentIndex + 1} / ${photos.length}`}</div>
            <div className={styles.content}>
              {!!photos[currentIndex].title && <div className={styles.title}>{photos[currentIndex].title}</div>}
              <div className={styles.credits}>{photos[currentIndex].credits}</div>
            </div>
          </div>
        </>
      ) : (
        <div className={styles.placeholder}>
          <PkSpinner size={70} />
        </div>
      )}
    </div>
  );
};

export default memo(PhotoCarousel);
